import React, { useMemo, useState } from 'react';
import { IconBrandDiscordFilled } from '@tabler/icons-react';
import {
  BadgeDollarSign,
  Box,
  Bot,
  Server,
  Check,
  ChevronUp,
  Download,
  Loader2,
  LogOut,
  MonitorCog,
  MoonStar,
  SunMedium,
  Ticket,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import type { AuthUser, WebsiteCredits } from '../../lib/auth';
import { DISCORD_COMMUNITY_PATH } from '../../lib/community-redirects';
import { useWebsiteTheme, type WebsiteTheme } from '../../lib/theme';
import { chatLucideIconProps } from './chat-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const creditFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

function getUserDisplayName(user: AuthUser) {
  const displayName = user.display_name?.trim();
  if (displayName) {
    return displayName;
  }

  const email = user.email.trim();
  return email.split('@')[0] || email;
}

function getUserInitials(user: AuthUser) {
  const label = getUserDisplayName(user);
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'B';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
}

function formatCredits(value: number) {
  if (!Number.isFinite(value)) {
    return '0';
  }

  return creditFormatter.format(value);
}

function formatPlanName(planCode: string) {
  if (planCode === 'signup_bonus') {
    return 'Signup Bonus';
  }

  return planCode
    .replace(/_/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());
}

function formatExpiryLabel(value?: string | null) {
  if (!value) {
    return '';
  }

  const expiresAt = new Date(value);
  if (Number.isNaN(expiresAt.getTime())) {
    return '';
  }

  return `Expires ${new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(expiresAt)}`;
}

function getUsageBarClass(progress: number) {
  if (progress > 0.5) {
    return 'bg-emerald-500';
  }
  if (progress > 0.2) {
    return 'bg-amber-500';
  }
  return 'bg-rose-500';
}

function ThemeMenuItem({
  theme,
  currentTheme,
  label,
  icon,
  onSelect,
}: {
  theme: WebsiteTheme;
  currentTheme: WebsiteTheme;
  label: string;
  icon: React.ReactNode;
  onSelect: (theme: WebsiteTheme) => void;
}) {
  return (
    <DropdownMenuItem
      className="cursor-pointer justify-between px-2 py-1.5 text-[13px]"
      onSelect={() => onSelect(theme)}
    >
      <span className="flex items-center gap-2">
        {icon}
        {label}
      </span>
      <Check
        {...chatLucideIconProps}
        className={cn(
          'size-4',
          currentTheme === theme ? 'opacity-100' : 'opacity-0'
        )}
      />
    </DropdownMenuItem>
  );
}

function ChatSidebarAccountActionButton({
  icon,
  label,
  href,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      {icon}
      <span className="min-w-0 truncate">{label}</span>
    </>
  );
  const className =
    'flex h-9 w-full min-w-0 items-center justify-center gap-1.5 rounded-lg border border-border/45 bg-background/60 px-2.5 text-[13px] font-medium text-foreground shadow-[0_1px_2px_rgba(23,23,23,0.04)] transition-colors duration-150 hover:border-border/70 hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ring/20 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]';

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  );
}

export function ChatSidebarAccount({
  user,
  credits,
  loadingCredits,
  onLogout,
  onOpenBilling,
  onOpenRedeem,
  onOpenComputers,
  onOpenBook,
  onOpenIntegrations,
  onDownloadClient,
}: {
  user: AuthUser;
  credits?: WebsiteCredits | null;
  loadingCredits: boolean;
  onLogout: () => Promise<void>;
  onOpenBilling: () => void;
  onOpenRedeem: () => void;
  onOpenComputers: () => void;
  onOpenBook: () => void;
  onOpenIntegrations: () => void;
  onDownloadClient: () => void;
}) {
  const { theme, setTheme } = useWebsiteTheme();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const hasUsage = Boolean(credits);
  const availableCredits = credits?.available_credits ?? 0;
  const isOutOfCredits = hasUsage && availableCredits <= 0;
  const usagePlans = useMemo(() => credits?.plans ?? [], [credits]);

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    try {
      await onLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="px-2.5 pb-2.5 pt-1.5">
      <div className="mb-1.5 flex flex-col gap-1.5">
        <ChatSidebarAccountActionButton
          icon={
            <IconBrandDiscordFilled
              className="size-4 shrink-0 text-[#5865F2]"
              aria-hidden
            />
          }
          label="Discord"
          href={DISCORD_COMMUNITY_PATH}
        />
        <ChatSidebarAccountActionButton
          icon={
            <Download
              {...chatLucideIconProps}
              className="size-4 shrink-0 text-muted-foreground"
            />
          }
          label="Download app"
          onClick={onDownloadClient}
        />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Open account menu"
            className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors duration-150 hover:bg-black/6 focus-visible:ring-2 focus-visible:ring-ring/20 dark:hover:bg-white/6"
          >
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={getUserDisplayName(user)}
                className="size-8 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-foreground/75">
                {getUserInitials(user)}
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="chat-type-body-sm-strong truncate text-foreground">
                {getUserDisplayName(user)}
              </div>
              <div className="truncate text-[11px] text-muted-foreground">
                {user.email}
              </div>
            </div>

            <ChevronUp
              {...chatLucideIconProps}
              className="size-4 shrink-0 text-muted-foreground"
            />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          side="top"
          sideOffset={10}
          collisionPadding={12}
          className="w-[292px] max-w-[calc(100vw-24px)] p-1.5"
        >
          <div className="px-2 py-1.5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Usage
                </div>
                <div className="mt-1 text-[15px] font-semibold text-foreground">
                  {loadingCredits
                    ? 'Loading…'
                    : hasUsage
                      ? formatCredits(availableCredits)
                      : 'Unavailable'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {loadingCredits
                    ? 'Checking remaining credits'
                    : hasUsage
                      ? isOutOfCredits
                        ? 'credits exhausted'
                        : 'credits remaining'
                      : 'Unable to load usage'}
                </div>
              </div>

              {credits?.plan_code ? (
                <div className="text-[10px] font-medium text-muted-foreground">
                  {formatPlanName(credits.plan_code)}
                </div>
              ) : null}
            </div>

            {loadingCredits ? null : usagePlans.length > 0 ? (
              <div className="mt-2 flex max-h-[180px] flex-col gap-1 overflow-y-auto">
                {usagePlans.map((plan, index) => {
                  const isPlanExhausted = plan.remaining_credits <= 0;
                  const progress =
                    plan.total_credits > 0
                      ? Math.max(
                          0,
                          Math.min(
                            1,
                            plan.remaining_credits / plan.total_credits
                          )
                        )
                      : 0;

                  return (
                    <div
                      key={plan.grant_id ?? `${plan.plan_code}-${index}`}
                      className="py-0.5"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="truncate text-[11px] font-medium text-foreground">
                          {formatPlanName(plan.plan_code)}
                        </div>
                        {plan.expires_at ? (
                          <div className="shrink-0 text-[10px] text-muted-foreground">
                            {formatExpiryLabel(plan.expires_at)}
                          </div>
                        ) : null}
                      </div>

                      <div className="mt-1 flex items-center justify-between gap-3 text-[10px] text-muted-foreground">
                        <span>
                          {isPlanExhausted
                            ? 'Used up'
                            : `${formatCredits(plan.remaining_credits)} left`}
                        </span>
                        <span>{formatCredits(plan.total_credits)} total</span>
                      </div>

                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-black/6 dark:bg-white/8">
                        <div
                          className={cn(
                            'h-full rounded-full transition-[width]',
                            getUsageBarClass(progress)
                          )}
                          style={{
                            width: `${progress > 0 ? Math.max(progress * 100, 4) : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : hasUsage && isOutOfCredits ? (
              <div className="mt-3 rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                Current credits are exhausted. Get more credits or redeem a code
                below.
              </div>
            ) : (
              <div className="mt-3 text-xs text-muted-foreground">
                Usage details are not available yet.
              </div>
            )}
          </div>

          <DropdownMenuSeparator />

          <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Billing
          </div>

          <DropdownMenuItem
            className="cursor-pointer px-2 py-1.5 text-[13px]"
            onSelect={onOpenBilling}
          >
            <BadgeDollarSign {...chatLucideIconProps} className="size-4" />
            <span className="flex min-w-0 flex-col leading-tight">
              <span>Get more credits</span>
              <span className="text-[11px] text-muted-foreground">
                Explore plans and top-up add-ons
              </span>
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer px-2 py-1.5 text-[13px]"
            onSelect={onOpenRedeem}
          >
            <Ticket {...chatLucideIconProps} className="size-4" />
            <span className="flex min-w-0 flex-col leading-tight">
              <span>Redeem code</span>
              <span className="text-[11px] text-muted-foreground">
                Apply a promo or team credit code
              </span>
            </span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Theme
          </div>
          <ThemeMenuItem
            theme="system"
            currentTheme={theme}
            label="System"
            icon={<MonitorCog {...chatLucideIconProps} className="size-4" />}
            onSelect={setTheme}
          />
          <ThemeMenuItem
            theme="light"
            currentTheme={theme}
            label="Light"
            icon={<SunMedium {...chatLucideIconProps} className="size-4" />}
            onSelect={setTheme}
          />
          <ThemeMenuItem
            theme="dark"
            currentTheme={theme}
            label="Dark"
            icon={<MoonStar {...chatLucideIconProps} className="size-4" />}
            onSelect={setTheme}
          />

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="cursor-pointer px-2 py-1.5 text-[13px]"
            onSelect={onOpenBook}
          >
            <Box {...chatLucideIconProps} className="size-4" />
            <span className="flex min-w-0 flex-col leading-tight">
              <span>Books</span>
              <span className="text-[11px] text-muted-foreground">
                Browse generated artifacts
              </span>
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer px-2 py-1.5 text-[13px]"
            onSelect={onOpenComputers}
          >
            <Server {...chatLucideIconProps} className="size-4" />
            <span className="flex min-w-0 flex-col leading-tight">
              <span>Connected computers</span>
              <span className="text-[11px] text-muted-foreground">
                Manage your bb connectors
              </span>
            </span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="cursor-pointer px-2 py-1.5 text-[13px]"
            onSelect={onOpenIntegrations}
          >
            <Bot {...chatLucideIconProps} className="size-4" />
            <span className="flex min-w-0 flex-col leading-tight">
              <span>Integrations</span>
              <span className="text-[11px] text-muted-foreground">
                Slack, Discord & Telegram
              </span>
            </span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            variant="destructive"
            className="cursor-pointer px-2 py-1.5 text-[13px]"
            disabled={isLoggingOut}
            onSelect={() => {
              void handleLogout();
            }}
          >
            {isLoggingOut ? (
              <Loader2
                {...chatLucideIconProps}
                className="size-4 animate-spin"
              />
            ) : (
              <LogOut {...chatLucideIconProps} className="size-4" />
            )}
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
